import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { ScholarshipProgram } from '../types';
import Modal from './Modal';
import ProfileDocuments from './ProfileDocuments';

/**
 * Uploaded in Section I of the Profile page (ValidIdUpload), not in the
 * document list below — ProfileDocuments deliberately filters it out so
 * it isn't offered twice. It still counts towards the requirement, so if
 * it is the thing missing, this dialog has to send the student to the
 * page that can actually take it. Without that they would be blocked
 * here by a row that does not exist here.
 */
const VALID_ID_TYPE = 'Valid ID';

interface Props {
  scholarship: ScholarshipProgram;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Just creates the draft application. School, course, address, and year
 * level all moved to the full Profile page — collecting them here too
 * was redundant, since the completeness gate already requires them there
 * before the application can be submitted.
 *
 * The required documents, though, are required here: the server refuses
 * to create the draft while any are missing, so this dialog checks the
 * same thing up front and says which ones rather than letting the button
 * fail. The upload rows are already on this screen, so the fix for the
 * refusal is in reach of the refusal itself.
 */
const ApplyModal = ({ scholarship, onClose, onSuccess }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [missingDocuments, setMissingDocuments] = useState<string[] | null>(null);
  // This programme's own required documents — the only ones that gate
  // this application. Null while loading, which is not the same as an
  // empty list: an empty list means the programme requires nothing, and
  // rendering that before the fetch returns would flash "no documents
  // needed" at someone who is about to be told otherwise.
  const [programTypes, setProgramTypes] = useState<string[] | null>(null);
  const [programTypesFailed, setProgramTypesFailed] = useState(false);

  useEffect(() => {
    apiService
      .getRequiredDocuments(scholarship.id)
      .then((docs) => setProgramTypes(docs.map((d) => d.documentType).filter(Boolean)))
      .catch(() => {
        // The rows cannot be drawn without this list, so say so rather
        // than showing an empty one. The server still enforces the real
        // requirement either way.
        setProgramTypes([]);
        setProgramTypesFailed(true);
      });
  }, [scholarship.id]);

  // Re-read after every upload or delete, so the button unlocks as soon
  // as the last document lands rather than on a reopen of the dialog.
  const refreshCompleteness = useCallback(() => {
    apiService
      // Scholarship-aware, so what this dialog reports matches what
      // createApplication will actually enforce.
      .getProfileCompleteness(scholarship.id)
      .then((c) => setMissingDocuments(c.missingDocuments))
      .catch(() => {
        // Left null: unknown, not complete. The button stays available
        // and the server decides — a failure to read completeness must
        // not be the reason someone cannot apply.
        setMissingDocuments(null);
      });
  }, [scholarship.id]);

  useEffect(() => {
    refreshCompleteness();
  }, [refreshCompleteness]);

  const blocked = missingDocuments !== null && missingDocuments.length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      await apiService.createApplication(scholarship.id);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to submit application');
      // The refusal may be about documents — re-read so the list below
      // matches what the server just objected to.
      refreshCompleteness();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Apply: ${scholarship.name}`} onClose={onClose}>
      {error && (
        <div className="alert-error" style={{ padding: '0.75rem', marginBottom: '1rem', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      <p style={{ color: 'var(--text-secondary)' }}>
        This creates a draft application for <strong>{scholarship.name}</strong>. Complete your profile before
        submitting it for review.
      </p>

      {blocked && (
        <div
          style={{
            padding: '0.85rem 1rem',
            marginBottom: '1rem',
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--warning-text)',
            fontSize: '0.85rem'
          }}
          role="alert"
        >
          <p style={{ margin: '0 0 0.4rem', fontWeight: 600 }}>
            Upload {missingDocuments!.length} more document{missingDocuments!.length === 1 ? '' : 's'} to apply
          </p>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {missingDocuments!.map((doc) => (
              <li key={doc}>{doc}</li>
            ))}
          </ul>
          {missingDocuments!.includes(VALID_ID_TYPE) && (
            <p style={{ margin: '0.5rem 0 0' }}>
              Your Valid ID is uploaded on your <Link to="/profile">Profile page</Link>, under Section I — not
              in the list below.
            </p>
          )}
        </div>
      )}

      <div style={{ marginBottom: '1.25rem' }}>
        <strong style={{ fontSize: '0.85rem' }}>Documents required by this program</strong>
        {programTypes === null ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
            Loading...
          </p>
        ) : programTypesFailed ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--error-text)', margin: '0.25rem 0 0' }}>
            The document list could not be loaded. Please reload the page.
          </p>
        ) : programTypes.length === 0 ? (
          // A programme is allowed to ask for nothing. Said out loud so
          // an empty space does not read as a list that failed to load.
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
            This program does not require any documents to apply.
          </p>
        ) : (
          <>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0.6rem' }}>
              All of these are required by this program. Each one is saved to your profile, so it's reused for
              every application and also appears under "VIII. Documentary Requirements" on your Profile page.
            </p>
            <ProfileDocuments onChange={refreshCompleteness} requiredTypes={programTypes} />
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
        <button
          className="btn btn-primary"
          type="button"
          // Disabled only when we know something is missing. The server
          // is the actual gate; this stops a pointless round trip and,
          // with the list above, says why the button is unavailable —
          // which a disabled button alone never does.
          disabled={submitting || blocked}
          onClick={handleSubmit}
        >
          {submitting ? 'Applying...' : 'Apply'}
        </button>
        <button className="btn btn-outline" type="button" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default ApplyModal;
